import React, { useState, useEffect, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import { OilImage } from '../components/GameImages';
import '../css.css';

// 등급을 별표로 표시하는 컴포넌트
const StarRating = ({ rating }) => {
    const numRating = parseFloat(rating) || 0;
    const stars = [];
    
    for (let i = 1; i <= 5; i++) {
        const diff = numRating - (i - 1);
        let starType;
        
        if (diff >= 1) {
            starType = 'star-full.svg';
        } else if (diff >= 0.5) {
            starType = 'star-half.svg';
        } else {
            starType = 'star-empty.svg';
        }
        
        stars.push(
            <img
                key={i}
                src={`${process.env.PUBLIC_URL}/icon/svg/${starType}`}
                alt={`star-${i}`}
                style={{
                    width: '16px',
                    height: '16px',
                    marginRight: '1px'
                }}
            />
        );
    }
    
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {stars}
        </div>
    );
};

// 성능 그래프 컴포넌트
const PerformanceGraph = ({ oil, minMaxValues }) => {
    const price = parseFloat(oil.price) || 0;
    const durability = parseFloat(oil.durability) || 0;
    const distance = parseFloat(oil.distance) || 0;
    
    // 가격은 역순으로 계산 (낮은 가격이 높은 점수)
    const priceScore = price > 0 ? minMaxValues.price.max - price + minMaxValues.price.min : 0;
    
    // 값을 각 항목의 최소/최대값 기준으로 10%-90% 범위로 정규화하는 함수
    const normalizeValueByRange = (value, min, max) => {
        if (max === min) return 50; // 모든 값이 같으면 중간값
        const percentage = Math.max(0, Math.min(1, (value - min) / (max - min)));
        return 10 + (percentage * 80); // 10% + (0~1 * 80%) = 10% ~ 90%
    };
    
    // 값에 따라 색상을 결정하는 함수
    const getColorByValue = (percentage) => {
        if (percentage <= 40) {
            return '#ff4444';
        } else if (percentage <= 70) {
            return '#ff8c00';
        } else {
            return '#4caf50';
        }
    };
    
    const BarItem = ({ label, value, displayValue }) => {
        const barColor = getColorByValue(value);
        
        return (
            <div style={{ marginBottom: '4px' }}>
                <div style={{ fontSize: '10px', marginBottom: '2px' }}>
                    {label}: {displayValue}
                </div>
                <div style={{
                    height: '8px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #ddd',
                    borderRadius: '2px',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        width: `${value}%`,
                        height: '100%',
                        background: `linear-gradient(90deg, ${barColor}aa, ${barColor}, ${barColor}dd)`,
                        transition: 'width 0.3s ease'
                    }} />
                </div>
            </div>
        );
    };

    return (
        <div style={{ fontSize: '11px' }}>
            <BarItem 
                label="가격" 
                value={normalizeValueByRange(priceScore, minMaxValues.price.min, minMaxValues.price.max)} 
                displayValue={price}
            />
            <BarItem 
                label="내구성" 
                value={normalizeValueByRange(durability, minMaxValues.durability.min, minMaxValues.durability.max)} 
                displayValue={`${durability}%`}
            />
            <BarItem 
                label="비거리" 
                value={normalizeValueByRange(distance, minMaxValues.distance.min, minMaxValues.distance.max)} 
                displayValue={`${distance}%`}
            />
        </div>
    );
};

const Page1 = () => {
    const [oilData, setOilData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchOilData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch('/api/oil');
            if (!response.ok) throw new Error('Network response was not ok');
            
            const data = await response.json();
            setOilData(data);
        } catch (error) {
            console.error('Error fetching oil data:', error);
            setError('데이터를 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOilData();
    }, [fetchOilData]);

    // 전체 데이터에서 각 항목의 최소값과 최대값을 계산
    const getMinMaxValues = React.useMemo(() => {
        if (oilData.length === 0) return null;
        
        const prices = oilData.map(oil => parseFloat(oil.price) || 0).filter(p => p > 0);
        const durabilities = oilData.map(oil => parseFloat(oil.durability) || 0);
        const distances = oilData.map(oil => parseFloat(oil.distance) || 0);

        return {
            price: {
                min: Math.min(...prices),
                max: Math.max(...prices)
            },
            durability: {
                min: Math.min(...durabilities),
                max: Math.max(...durabilities)
            },
            distance: {
                min: Math.min(...distances),
                max: Math.max(...distances)
            }
        };
    }, [oilData]);

    // viscosity별로 그룹화
    const groupedData = React.useMemo(() => {
        const groups = {
            default: { name: '브랜드 별 기본 오일', items: [] },
            low: { name: '저점도 오일', items: [] },
            mid: { name: '중점도 오일', items: [] },
            high: { name: '고점도 오일', items: [] }
        };

        oilData.forEach(oil => {
            const viscosity = oil.viscosity || 'default';
            if (groups[viscosity]) {
                groups[viscosity].items.push(oil);
            } else {
                groups.default.items.push(oil);
            }
        });

        return groups;
    }, [oilData]);

    if (loading) {
        return (
            <>
                <h1 className="page-title">릴 오일 정보</h1>
                <div className="spinner-container">
                    <LoadingSpinner height={80} width={80} color="#4fa94d" visible={true} />
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <h1 className="page-title">릴 오일 정보</h1>
                <div className="error-message" style={{ textAlign: 'center', padding: '20px', color: '#ff4444' }}>
                    {error}
                    <button onClick={fetchOilData} style={{ marginLeft: '10px', padding: '5px 10px' }}>
                        다시 시도
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <h1 className="page-title">릴 오일 정보</h1>
            <div className="page-content">
                {Object.entries(groupedData)
                    .filter(([key, group]) => group.items.length > 0)
                    .map(([key, group]) => (
                    <div key={key} style={{ marginBottom: '40px' }}>
                        <h2 className="group-title">
                            {group.name}
                        </h2>
                        <table className="oil-table" style={{ marginBottom: '20px' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '80px' }}>사진</th>
                                    <th style={{ width: '120px' }}>제조사</th>
                                    <th style={{ width: '150px' }}>이름</th>
                                    <th style={{ width: '80px' }}>등급</th>
                                    <th style={{ width: '80px' }}>가격</th>
                                    <th style={{ width: '80px' }}>내구성</th>
                                    <th style={{ width: '80px' }}>비거리</th>
                                    <th style={{ width: '200px' }}>평가</th>
                                    <th style={{ minWidth: '200px' }}>그래프</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.items.map((oil, index) => (
                                    <tr key={oil.id || index}>
                                        <td style={{ textAlign: 'center', padding: '10px' }}>
                                            <OilImage
                                                filename={oil.filename}
                                                alt={oil.name}
                                                className="oil-table-image"
                                            />
                                        </td>
                                        <td style={{ padding: '10px' }}>{oil.manufacturer}</td>
                                        <td style={{ padding: '10px', fontWeight: 'bold' }}>{oil.name}</td>
                                        <td style={{ textAlign: 'center', padding: '10px' }}>
                                            <StarRating rating={oil.recommand} />
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '10px', fontWeight: 'bold', color: '#2c5530' }}>
                                            {oil.price}
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '10px' }}>{oil.durability}%</td>
                                        <td style={{ textAlign: 'center', padding: '10px' }}>{oil.distance}%</td>
                                        <td style={{
                                            textAlign: 'left',
                                            whiteSpace: 'pre',
                                            lineHeight: '1.3',
                                            padding: '6px 4px'
                                        }}>
                                            {oil.evaluation}
                                        </td>
                                        <td style={{ padding: '10px' }}>
                                            {getMinMaxValues && (
                                                <PerformanceGraph oil={oil} minMaxValues={getMinMaxValues} />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
            <Comments pageName="oil" />
        </>
    );
};

export default Page1;
